import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ExternalLink, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { Pill, when } from './PlatformConsole';
import { ConfirmAction } from './PlatformPharmacies';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const PlatformKyc = () => {
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/platform/kyc', { params: { status } }).then(r => setData(r.data))
      .catch(() => toast.error('Could not load the KYC queue'));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const review = async (decision, doc, reason) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/platform/kyc/${doc.doc_id}/review`, { decision, reason });
      toast.success(`Document ${decision} · pharmacy KYC now ${res.pharmacy_kyc_status}`);
      setDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Review failed'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="pc-muted"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  return (
    <div data-testid="pc-kyc">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1 className="pc-h1">Pharmacy KYC</h1>
          <p className="pc-sub">{data.count} document(s) · {data.expiring_soon.length} licence(s) expiring within 60 days</p>
        </div>
        <select className="pc-select" style={{ width: 160 }} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="pc-kyc-filter">
          {['pending', 'approved', 'rejected', 'all'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="pc-card pc-scroll" style={{ marginTop: '1.1rem' }}>
        <table className="pc-table">
          <thead><tr><th>Pharmacy</th><th>Document</th><th>Expiry</th><th>Uploaded</th><th>Status</th><th /></tr></thead>
          <tbody>
            {data.documents.map(d => (
              <tr key={d.doc_id} data-testid={`pc-kyc-row-${d.doc_id}`}>
                <td><Link to={`/platform/pharmacies/${d.tenant_id}`} style={{ color: '#fff' }}>{d.pharmacy_name || d.tenant_id}</Link>
                  <div className="pc-muted">pharmacy KYC: {d.pharmacy_kyc_status}</div></td>
                <td>{d.label}<div className="pc-mono">{d.filename}</div></td>
                <td>{d.expiry_date || '—'}</td>
                <td>{when(d.uploaded_at)}</td>
                <td><Pill value={d.status} />{d.reason && <div className="pc-muted">“{d.reason}”</div>}</td>
                <td className="pc-num" style={{ whiteSpace: 'nowrap' }}>
                  <a className="pc-btn" href={`${API_BASE}/api/platform/kyc/${d.doc_id}/file`} target="_blank"
                     rel="noreferrer" data-testid={`pc-kyc-view-${d.doc_id}`}>
                    View <ExternalLink className="h-3 w-3 inline" />
                  </a>{' '}
                  <button className="pc-btn pc-btn-primary" onClick={() => setDialog({ doc: d, decision: 'approved' })}
                    data-testid={`pc-kyc-approve-${d.doc_id}`}>Approve</button>{' '}
                  <button className="pc-btn pc-btn-danger" onClick={() => setDialog({ doc: d, decision: 'rejected' })}
                    data-testid={`pc-kyc-reject-${d.doc_id}`}>Reject</button>
                </td>
              </tr>
            ))}
            {data.documents.length === 0 && (
              <tr><td colSpan={6} className="pc-muted" style={{ textAlign: 'center', padding: '2rem' }} data-testid="pc-kyc-empty">
                Nothing in this queue.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {dialog && (
        <ConfirmAction
          open title={`${dialog.decision === 'approved' ? 'Approve' : 'Reject'} ${dialog.doc.label}?`}
          description={dialog.decision === 'approved'
            ? 'The pharmacy is emailed. Once every document is approved the pharmacy can go live.'
            : 'A reason is required and is sent to the pharmacy.'}
          requireReason={dialog.decision === 'rejected'} busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={(reason) => review(dialog.decision, dialog.doc, reason)}
          testId="pc-kyc-confirm"
        />
      )}
    </div>
  );
};

export const PlatformCustomers = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/platform/customers').then(r => setData(r.data)).catch(() => setData({ directory: [], totals: {} })); }, []);
  if (!data) return <div className="pc-muted"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  return (
    <div data-testid="pc-customers">
      <h1 className="pc-h1">Customer directory</h1>
      <p className="pc-sub">Volumes and verification state only — patient identity documents stay with the pharmacy.</p>
      <div className="pc-grid">
        <div className="pc-tile"><b>{(data.totals.customers || 0).toLocaleString()}</b><span>Customers</span></div>
        <div className="pc-tile"><b>{(data.totals.portal_signups || 0).toLocaleString()}</b><span>Portal sign-ups</span></div>
        <div className="pc-tile"><b>{(data.totals.verified || 0).toLocaleString()}</b><span>Verified</span></div>
      </div>
      <div className="pc-card pc-scroll">
        <table className="pc-table">
          <thead><tr><th>Pharmacy</th><th className="pc-num">Customers</th><th className="pc-num">Portal sign-ups</th>
            <th className="pc-num">Verified</th><th className="pc-num">Pending</th><th className="pc-num">Verified %</th></tr></thead>
          <tbody>
            {data.directory.map(r => (
              <tr key={r.tenant_id} data-testid={`pc-customers-row-${r.tenant_id}`}>
                <td><Link to={`/platform/pharmacies/${r.tenant_id}`} style={{ color: '#fff' }}>{r.pharmacy_name || r.tenant_id}</Link></td>
                <td className="pc-num">{r.customers.toLocaleString()}</td>
                <td className="pc-num">{r.portal_signups.toLocaleString()}</td>
                <td className="pc-num">{r.verified.toLocaleString()}</td>
                <td className="pc-num">{r.pending.toLocaleString()}</td>
                <td className="pc-num">{r.verified_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const PlatformLeads = () => {
  const [status, setStatus] = useState('');
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/platform/leads', { params: { status } }).then(r => setData(r.data))
      .catch(() => toast.error('Could not load leads'));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const move = async (lead, next, note) => {
    setBusy(true);
    try {
      await api.post(`/platform/leads/${lead.lead_id}/status`, { status: next, note });
      toast.success(`Lead marked ${next.replace('_', ' ')}`);
      setDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="pc-muted"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  return (
    <div data-testid="pc-leads">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1 className="pc-h1">Demo requests</h1>
          <p className="pc-sub">{Object.entries(data.counts).map(([k, v]) => `${v} ${k.replace('_', ' ')}`).join(' · ')}</p>
        </div>
        <select className="pc-select" style={{ width: 160 }} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="pc-leads-filter">
          <option value="">All</option>
          {data.statuses.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
      </div>
      <div className="pc-card pc-scroll" style={{ marginTop: '1.1rem' }}>
        <table className="pc-table">
          <thead><tr><th>Pharmacy</th><th>Contact</th><th>Town</th><th className="pc-num">Branches</th>
            <th>Using now</th><th>Received</th><th>Status</th><th /></tr></thead>
          <tbody>
            {data.leads.map(l => (
              <tr key={l.lead_id} data-testid={`pc-lead-row-${l.lead_id}`}>
                <td><b style={{ color: '#fff' }}>{l.pharmacy_name}</b>
                  {l.interest && <div className="pc-muted">“{l.interest.slice(0, 90)}”</div>}</td>
                <td>{l.contact_name}<div className="pc-mono">{l.email}{l.phone ? ` · ${l.phone}` : ''}</div></td>
                <td>{l.town || '—'}</td>
                <td className="pc-num">{l.branches}</td>
                <td>{l.current_software || '—'}</td>
                <td>{when(l.created_at)}</td>
                <td><Pill value={l.status === 'won' ? 'approved' : l.status === 'lost' ? 'rejected' : 'pending'} />
                  <div className="pc-muted">{l.status.replace('_', ' ')}</div>
                  {(l.notes || []).slice(-1).map((n, i) => <div key={i} className="pc-muted">“{n.note}”</div>)}</td>
                <td className="pc-num">
                  <button className="pc-btn" onClick={() => setDialog({ lead: l, next: 'contacted' })} data-testid={`pc-lead-contacted-${l.lead_id}`}>Contacted</button>{' '}
                  <button className="pc-btn" onClick={() => setDialog({ lead: l, next: 'demo_booked' })} data-testid={`pc-lead-demo-${l.lead_id}`}>Demo booked</button>{' '}
                  <button className="pc-btn pc-btn-primary" onClick={() => setDialog({ lead: l, next: 'won' })} data-testid={`pc-lead-won-${l.lead_id}`}>Won</button>{' '}
                  <button className="pc-btn pc-btn-danger" onClick={() => setDialog({ lead: l, next: 'lost' })} data-testid={`pc-lead-lost-${l.lead_id}`}>Lost</button>
                </td>
              </tr>
            ))}
            {data.leads.length === 0 && (
              <tr><td colSpan={8} className="pc-muted" style={{ textAlign: 'center', padding: '2rem' }} data-testid="pc-leads-empty">
                No demo requests yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {dialog && (
        <ConfirmAction
          open title={`Mark ${dialog.lead.pharmacy_name} as ${dialog.next.replace('_', ' ')}?`}
          description="The note is kept on the lead's history." requireReason={false} busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() => move(dialog.lead, dialog.next, dialog.note || '')}
          testId="pc-lead-confirm"
        >
          <label className="pc-field">Note (optional)</label>
          <textarea className="pc-textarea" rows={2} onChange={(e) => { dialog.note = e.target.value; }} data-testid="pc-lead-note" />
        </ConfirmAction>
      )}
    </div>
  );
};

export const PlatformAudit = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get('/platform/audit').then(r => setRows(r.data.audit || [])).catch(() => setRows([])); }, []);
  if (!rows) return <div className="pc-muted"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  return (
    <div data-testid="pc-audit">
      <h1 className="pc-h1">Audit trail</h1>
      <p className="pc-sub">Every financial and destructive action taken from this console.</p>
      <div className="pc-card pc-scroll" style={{ marginTop: '1.1rem' }}>
        <table className="pc-table">
          <thead><tr><th>When</th><th>Action</th><th>Pharmacy</th><th>Detail</th><th>Reason</th><th>Actor</th></tr></thead>
          <tbody>
            {rows.map(a => (
              <tr key={a.audit_id} data-testid={`pc-audit-${a.audit_id}`}>
                <td className="pc-mono">{when(a.created_at)}</td>
                <td>{a.action}</td>
                <td className="pc-mono">{a.tenant_id || '—'}</td>
                <td className="pc-muted">{a.detail || '—'}</td>
                <td className="pc-muted">{a.reason || '—'}</td>
                <td className="pc-muted">{a.actor_name}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="pc-muted" style={{ textAlign: 'center', padding: '2rem' }}>Nothing yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
